// The schedule-call station: the same scheduler the PhoneBand/nav modal
// shows, as a full screen, with the after-www gate's secondary path
// beneath it for a family not ready to pick a time. Reached only when
// the quiz concluded on a gated preplan timing (the server derives this
// station; see quote-step.ts on after-internal) — the existing modal is
// unchanged and still goes straight to the application on booking.

import { useEffect, useRef } from "react";
import { usePostHog } from "posthog-js/react";

import type { QuoteCheckpoint, ScheduledCall } from "./api";
import BookingConfirmation from "./BookingConfirmation";
import { captureBookingConfirmationExposure } from "./booking-confirmation-exposure";
import {
  bookingConfirmationTitleHead,
  BOOKING_CONFIRMATION_COPY,
} from "./booking-confirmation-copy";
import { useQuoteFlowAnalytics } from "./analytics";
import ArrivalLoadingScreen from "./ArrivalLoadingScreen";
import {
  LINK_BUTTON_CLASS,
  OUTLINE_BUTTON_CLASS,
} from "components/FlowButtons";
import { deferralButtonLabel } from "./deferral-copy";
import type {
  BookingConfirmationVariant,
  ScheduleCallDeferralCopyVariant,
} from "./flags";
import { fromCheckpoints } from "./pre-call-answers";
import { repFirstName, resolveCallRep } from "./specialists";
import { bookedDayLabel, browserTimeZone, slotTimeLabel } from "./call-time";
import ScreenChrome from "components/ScreenChrome";
import ScheduleCallBody, { SCHEDULER_BLURB } from "./ScheduleCallBody";

export interface ScheduleCallStepProps {
  quoteId: string;
  contactPhone: string | null;
  credentialPending: boolean;
  existingCall: ScheduledCall | null;
  onBooked: (call: ScheduledCall) => void;
  /** The booked view's Continue on this station — writes the call-gate
   * checkpoint as booked and moves on. */
  onBookedContinue: () => void;
  /** "Not ready to pick a time?" — writes the call-gate checkpoint as
   * deferred and moves on. Hidden once a call is booked. */
  onDeferred: () => void;
  /** Back to the quiz's last question. */
  onBack: () => void;
  deferralCopyVariant: ScheduleCallDeferralCopyVariant;
  /** Which booked face this station shows: `control` is the scheduler's
   * own, `test` is the full confirmation. Null until the flags commit; a
   * booked station shows the loading mark until then. */
  bookingConfirmationVariant: BookingConfirmationVariant | null;
  marketSlug: string | null;
  /** The view's current checkpoint rows — the confirmation restores the
   * family's pre-call answers from them. */
  checkpoints: QuoteCheckpoint[];
  onCheckpointSaved: (checkpoint: QuoteCheckpoint) => void;
  /** The confirmation's Start my application: writes the call gate as
   * booked and enters the application. */
  onStartApplication: () => Promise<void>;
}

export default function ScheduleCallStep({
  quoteId,
  contactPhone,
  credentialPending,
  existingCall,
  onBooked,
  onBookedContinue,
  onDeferred,
  onBack,
  deferralCopyVariant,
  bookingConfirmationVariant,
  marketSlug,
  checkpoints,
  onCheckpointSaved,
  onStartApplication,
}: ScheduleCallStepProps) {
  const { track } = useQuoteFlowAnalytics();
  const posthog = usePostHog();
  const booked = existingCall !== null;
  // Set when the family books on this station. Only that booking is an
  // exposure; landing on an already-booked station is not.
  const bookedHereRef = useRef(false);

  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    track("quote_flow_call_gate_shown", {});
    track("appointment_gate_shown", {});
  }, [track]);

  const confirmationShownRef = useRef(false);
  useEffect(() => {
    if (!booked || bookingConfirmationVariant === null) return;
    if (confirmationShownRef.current) return;
    confirmationShownRef.current = true;
    track("booking_confirmation_shown", {
      variant: bookingConfirmationVariant,
    });
    if (bookedHereRef.current) {
      captureBookingConfirmationExposure(
        posthog,
        quoteId,
        bookingConfirmationVariant,
      );
    }
  }, [booked, bookingConfirmationVariant, track, posthog, quoteId]);

  // A booking made on this page swaps the picker for the confirmation in
  // place: the page returns to the top and the heading takes focus. A
  // landing on an already-booked station starts at the top as it is.
  const bookedOnArrivalRef = useRef(booked);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const swapsToConfirmation = booked && bookingConfirmationVariant === "test";
  useEffect(() => {
    if (!swapsToConfirmation || bookedOnArrivalRef.current) return;
    window.scrollTo({ top: 0, behavior: "instant" });
    titleRef.current?.focus({ preventScroll: true });
  }, [swapsToConfirmation]);

  if (booked && bookingConfirmationVariant === null) {
    return <ArrivalLoadingScreen />;
  }

  if (existingCall !== null && bookingConfirmationVariant === "test") {
    const rep = resolveCallRep(existingCall.host);
    return (
      <ScreenChrome
        kicker={BOOKING_CONFIRMATION_COPY.kicker}
        titleA={bookingConfirmationTitleHead(repFirstName(rep))}
        titleB={BOOKING_CONFIRMATION_COPY.titleTail}
        blurb=""
        titleRef={titleRef}
      >
        <BookingConfirmation
          quoteId={quoteId}
          call={existingCall}
          rep={rep}
          contactPhone={contactPhone}
          marketSlug={marketSlug}
          initialAnswers={fromCheckpoints(checkpoints)}
          onAnswersSaved={onCheckpointSaved}
          onStartApplication={onStartApplication}
        />
      </ScreenChrome>
    );
  }

  return (
    <ScreenChrome
      kicker="Schedule a call"
      titleA={booked ? "Your call is" : "Pick a time that"}
      titleB={booked ? "set" : "works for you"}
      blurb={booked ? "" : SCHEDULER_BLURB}
    >
      <div className="mt-7">
        <ScheduleCallBody
          headless
          quoteId={quoteId}
          contactPhone={contactPhone}
          credentialPending={credentialPending}
          existingCall={existingCall}
          onBooked={(call) => {
            bookedHereRef.current = true;
            onBooked(call);
            const timeZone = browserTimeZone();
            track("appointment_gate_completed", {
              formatted_date: bookedDayLabel(call.start, timeZone),
              formatted_time: slotTimeLabel(call.start, timeZone),
            });
            track("calendly_call_scheduled", {});
            posthog?.setPersonProperties({ hasScheduledAppointment: true });
          }}
          onBookedContinue={onBookedContinue}
        />
        <div className="mt-6">
          <button type="button" className={LINK_BUTTON_CLASS} onClick={onBack}>
            Back
          </button>
        </div>
        {!booked && (
          <div className="mt-8 flex flex-col items-center gap-2 border-t border-oat-200 pt-6 text-center">
            <p className="text-sm text-soil">Not ready to pick a time?</p>
            <button
              type="button"
              onClick={() => {
                track("pre_need_application_redirect_clicked", {
                  source: "appointment_gate",
                });
                onDeferred();
              }}
              className={OUTLINE_BUTTON_CLASS}
            >
              {deferralButtonLabel(deferralCopyVariant)}
            </button>
          </div>
        )}
      </div>
    </ScreenChrome>
  );
}
